import { useState } from "react";
import { toast } from "sonner";
import { BookOpen, Loader2, Check, Download } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const DAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const MAX = 7;

const shuffle = (a) => a.map((x) => [Math.random(), x]).sort((p, q) => p[0] - q[0]).map((p) => p[1]);
const variedPick = (games) => {
  const byCat = {};
  shuffle(games).forEach((g) => { (byCat[g.category] ||= []).push(g); });
  return Object.values(byCat).map((l) => l[0]);
};
const propose = (games) => {
  const managed = variedPick(games.filter((g) => !g.id.startsWith("builtin:")));
  const builtin = variedPick(games.filter((g) => g.id.startsWith("builtin:")));
  const out = [];
  while (out.length < MAX && (managed.length || builtin.length)) {
    const from = out.length % 2 ? builtin : managed;
    out.push((from.length ? from : managed.length ? managed : builtin).shift().id);
  }
  return out;
};

function GameRow({ g, index, onToggle, disabled }) {
  const on = index >= 0;
  return (
    <button onClick={onToggle} disabled={disabled} data-testid={`booklet-game-${g.id}`}
      className={`w-full flex items-center gap-3 rounded-2xl border-2 px-4 py-3 text-left transition-colors disabled:opacity-40 ${on ? "border-emerald-500 bg-emerald-50" : "border-gray-200 bg-white hover:border-emerald-200"}`}>
      <span className={`w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 ${on ? "bg-emerald-600 border-emerald-600 text-white" : "border-gray-300"}`}>
        {on && <Check className="w-4 h-4" />}
      </span>
      <span className="flex-1 font-semibold text-gray-800">{g.title}</span>
      {on && <span className="text-sm font-bold text-emerald-700" data-testid={`booklet-day-${g.id}`}>{DAYS[index]}</span>}
    </button>
  );
}

export default function BookletButton({ patient, className = "", testId = "booklet-button" }) {
  const [open, setOpen] = useState(false);
  const [games, setGames] = useState(null);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);

  const show = async () => {
    setOpen(true);
    try {
      const { data } = await api.get("/booklet/games");
      setGames(data); setPicked(propose(data));
    } catch { setGames([]); }
  };
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const download = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/patients/${patient.id}/booklet/pdf`, { game_ids: picked }, { responseType: "blob" });
      const href = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = href; a.download = `livret-de-la-semaine-${patient.name}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(href), 2000);
      toast.success("Le livret de la semaine est prêt à imprimer.");
      setOpen(false);
    } catch (e) {
      let msg = "Le livret n'a pas pu être créé.";
      try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* message par défaut */ }
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={show} data-testid={testId}
        className={`inline-flex items-center gap-2 rounded-full bg-white border-2 border-emerald-200 text-emerald-800 px-5 h-10 text-sm font-semibold hover:bg-emerald-50 transition-colors ${className}`}>
        <BookOpen className="w-4 h-4" /> Livret de la semaine
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="booklet-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl font-normal">Le livret de la semaine de {patient.name}</DialogTitle>
            <DialogDescription>Un jeu par jour, du lundi au dimanche, et tous les corrigés à la fin. Voici une proposition : retirez ou ajoutez des jeux (de 2 à 7).</DialogDescription>
          </DialogHeader>
          {games === null ? (
            <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-700" /></div>
          ) : games.length < 2 ? (
            <p className="text-gray-500 py-4" data-testid="booklet-empty">Il n'y a pas encore assez de jeux imprimables pour composer un livret.</p>
          ) : (
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-wide font-semibold text-gray-400">Dans le livret</p>
              {picked.map((id, i) => {
                const g = games.find((x) => x.id === id);
                return g && <GameRow key={id} g={g} index={i} onToggle={() => toggle(id)} />;
              })}
              {games.some((g) => !picked.includes(g.id)) && (
                <p className="text-xs uppercase tracking-wide font-semibold text-gray-400 pt-3">Autres jeux à ajouter</p>
              )}
              {games.filter((g) => !picked.includes(g.id)).map((g) => (
                <GameRow key={g.id} g={g} index={-1} onToggle={() => toggle(g.id)} disabled={picked.length >= MAX} />
              ))}
            </div>
          )}
          <div className="flex items-center justify-between gap-3 pt-2">
            <span className="text-sm text-gray-500" data-testid="booklet-count">{picked.length} jeu{picked.length > 1 ? "x" : ""} choisi{picked.length > 1 ? "s" : ""}</span>
            <Button onClick={download} disabled={busy || picked.length < 2} data-testid="booklet-download">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />} Télécharger le livret
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
